import React, { useRef } from "react";
import { Plus, MessageSquare, Sparkles, LayoutGrid, GitBranch, Zap, CircleStop, Mail, Clock, PhoneOff, Globe, Download, Upload, Phone, Sheet } from "lucide-react";
import { CUSTOM_NODE } from "../../../components/WorkFlow/types.js";
import useNodesInteractions from "../../../components/WorkFlow/hooks/use-nodes-interactions.js";
import useStore from "../../../components/WorkFlow/store/index.js";

const NODE_OPTIONS = [
  {
    type: CUSTOM_NODE.LanguageDetection,
    name: "Language Detection",
    icon: Globe,
    placeholder: false,
    accent: "purple",
  },
  {
    type: CUSTOM_NODE.HumanAgentHandoff,
    name: "Human Handoff",
    icon: PhoneOff,
    placeholder: false,
    accent: "blue",
  },
  {
    type: CUSTOM_NODE.Greeting,
    name: "Greeting",
    icon: Sparkles,
    placeholder: false,
    accent: "teal",
  },
  {
    type: CUSTOM_NODE.Conversation,
    name: "Conversation",
    icon: MessageSquare,
    placeholder: false,
    accent: "pink",
  },
  {
    type: CUSTOM_NODE.Sequence,
    name: "Question Group",
    icon: LayoutGrid,
    placeholder: false,
    accent: "blue",
  },
  {
    type: CUSTOM_NODE.HangUp,
    name: "End Call",
    icon: Phone,
    placeholder: false,
    accent: "red",
  },
  {
    type: CUSTOM_NODE.GoogleSheets,
    name: "Google Sheets",
    icon: Sheet,
    placeholder: false,
    accent: "green",
  },
  {
    type: "condition",
    name: "Condition",
    icon: GitBranch,
    placeholder: true,
  },
  {
    type: "action",
    name: "Action",
    icon: Zap,
    placeholder: true,
  },
  {
    type: "send_message",
    name: "Send message",
    icon: Mail,
    placeholder: true,
  },
  {
    type: "wait",
    name: "Wait",
    icon: Clock,
    placeholder: true,
  },
];

const NodesPanel = ({ variant = "dropdown" }) => {
  const { handleAddNode, handleLayout } = useNodesInteractions();
  const setNodes = useStore((state) => state.setNodes);
  const setEdges = useStore((state) => state.setEdges);
  const setAgent = useStore((state) => state.setAgent);
  const nodes = NODE_OPTIONS.filter((n) => !n.placeholder);
  const fileInputRef = useRef(null);

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!Array.isArray(data.nodes) || !Array.isArray(data.edges) || !data.agent) {
          throw new Error("Invalid flow data");
        }
        setNodes(data.nodes);
        setEdges(data.edges);
        setAgent(data.agent);
        alert(`✓ Imported: ${file.name}`);
      } catch (error) {
        alert(`✗ Import failed: ${error.message}`);
      }
    };
    reader.readAsText(file);
  };

  const handleExport = () => {
    try {
      const state = useStore.getState();
      const filename = `workflow_${new Date().toISOString().slice(0, 10)}.json`;
      const flowData = {
        nodes: state.nodes,
        edges: state.edges,
        agent: state.agentSetting,
      };
      const jsonString = JSON.stringify(flowData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      alert(`✓ Exported: ${filename}`);
    } catch (error) {
      alert(`✗ Export failed: ${error.message}`);
    }
  };

  if (variant === "sidebar") {
    return (
      <div className="h-full w-[180px] p-4 bg-white shadow-md rounded-r-lg shrink-0">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 mb-3">
          Add New Node
        </h2>
        <div className="flex flex-col gap-3">
          {nodes.map((item, index) => (
            <button
              key={index}
              className={`h-10 w-full flex items-center justify-center rounded-md text-sm font-medium transition text-white ${
                item.accent === "teal"
                  ? "bg-teal-600 hover:bg-teal-700"
                  : item.accent === "red"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-[#ec4899] hover:bg-[#db2777]"
              }`}
              onClick={() => handleAddNode(item.type)}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0.5 rounded-lg border border-gray-200/80 bg-white/95 shadow-sm py-1 px-1.5">
      <span className="flex items-center gap-1.5 pl-1.5 pr-2 py-1.5 text-xs font-medium text-gray-500 border-r border-gray-200/80 mr-1">
        <Plus className="w-3.5 h-3.5" />
        Add node
      </span>
      {NODE_OPTIONS.map((item, index) => {
        const Icon = item.icon;
        const isPlaceholder = item.placeholder;
        const accentClass =
          !isPlaceholder && item.accent === "teal"
            ? "text-teal-600 hover:bg-teal-50 hover:text-teal-800"
            : !isPlaceholder && item.accent === "red"
              ? "text-red-600 hover:bg-red-50 hover:text-red-800"
              : !isPlaceholder
                ? "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                : "";
        return (
          <button
            key={index}
            type="button"
            disabled={isPlaceholder}
            className={`flex items-center justify-center w-8 h-8 rounded-md transition ${isPlaceholder ? "text-gray-300 cursor-not-allowed" : accentClass}`}
            onClick={() => {
              if (!isPlaceholder) handleAddNode(item.type);
            }}
            title={isPlaceholder ? `${item.name} (coming soon)` : item.name}
          >
            <Icon className="w-4 h-4" />
          </button>
        );
      })}

      <span className="mx-1 h-6 w-px bg-gray-200/80" aria-hidden="true" />
      <button
        type="button"
        onClick={handleLayout}
        className="flex items-center justify-center w-8 h-8 rounded-md transition text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        title="Organize nodes"
        aria-label="Organize nodes"
      >
        <LayoutGrid className="w-4 h-4" />
      </button>

      <span className="mx-1 h-6 w-px bg-gray-200/80" aria-hidden="true" />

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        aria-label="Import workflow JSON"
      />

      {/* Import button */}
      <button
        type="button"
        onClick={handleImport}
        className="flex items-center justify-center w-8 h-8 rounded-md transition text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        title="Import JSON workflow"
        aria-label="Import workflow"
      >
        <Upload className="w-4 h-4" />
      </button>

      {/* Export button */}
      <button
        type="button"
        onClick={handleExport}
        className="flex items-center justify-center w-8 h-8 rounded-md transition text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        title="Export JSON workflow"
        aria-label="Export workflow"
      >
        <Download className="w-4 h-4" />
      </button>
    </div>
  );
};

export default NodesPanel;
